import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/admin :: DeleteQuestionButton
 *
 * Two-step confirm-then-delete pattern. Hard Rule #7 from the Wave 6b
 * spec: "Delete has two-step confirm. No single-click destruction." The
 * confirm surface is `role="dialog"` + `aria-modal` for keyboard users;
 * Escape dismisses; the initial focus lands on the cancel button so a
 * stray Enter doesn't destroy.
 */
export interface DeleteQuestionButtonProps {
  onConfirm: () => void | Promise<void>;
  disabled?: boolean;
  pending?: boolean;
  label?: string;
  /** Optional overrides for the confirm-dialog copy (used by callers that
   *  want slightly different wording on a list-row vs editor page). */
  titleKey?: string;
  bodyKey?: string;
}

export default function DeleteQuestionButton({
  onConfirm,
  disabled = false,
  pending = false,
  label,
  titleKey = "admin.editor.delete.confirmTitle",
  bodyKey = "admin.editor.delete.confirmBody",
}: DeleteQuestionButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="inline-block">
      <button
        type="button"
        data-testid="admin-delete-cta"
        disabled={disabled || pending}
        onClick={() => setOpen(true)}
        className="inline-flex items-center rounded-md border border-danger/60 bg-white px-sm py-xs text-sm font-medium text-danger transition-colors hover:bg-danger/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
      >
        {label ?? t("admin.editor.delete.cta")}
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          data-testid="admin-delete-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-md"
        >
          <div className="w-full max-w-md rounded-lg border border-brand-primary/10 bg-white p-lg shadow-xl">
            <h2
              id={titleId}
              className="font-display text-lg font-bold text-brand-primary"
            >
              {t(titleKey)}
            </h2>
            <p id={bodyId} className="mt-sm text-sm text-neutral-700">
              {t(bodyKey)}
            </p>
            <div className="mt-lg flex justify-end gap-sm">
              <button
                ref={cancelRef}
                type="button"
                data-testid="admin-delete-cancel"
                onClick={() => setOpen(false)}
                className="inline-flex items-center rounded-md border border-brand-primary/40 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
              >
                {t("admin.editor.delete.cancel")}
              </button>
              <button
                ref={confirmRef}
                type="button"
                data-testid="admin-delete-confirm"
                disabled={pending}
                onClick={() => {
                  void onConfirm();
                  setOpen(false);
                }}
                className="inline-flex items-center rounded-md border border-danger bg-danger px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-danger/90 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
              >
                {pending
                  ? t("admin.editor.delete.deleting")
                  : t("admin.editor.delete.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
